import {
	CANCELED_OPACITY, contrast,
	decodeNumericIconEntity,
	distributeGridSpace,
	DONE_OPACITY, DONE_SATURATION, drawHoliday,
	drawTextBadge, grayscale,
	insetRect,
	MOVE_OPACITY,
	parseHexColor,
	Rect,
	reduceSaturation,
	rgbToHex,
	roundFillShape,
	roundRect,
	Slot,
	SnapCoordinates
} from "@/event/calendar/Shapes.ts";
import {weekInfo} from "@fd/editors/temporal/CalendarWidgetTypes.ts";
import {dayNumeric} from "@/event/calendar/Formating.ts";
import {ICON_FONT, MONTH_GRID_OFFSET_X, MONTH_GRID_OFFSET_Y} from "@/event/calendar/Constants.ts";
import {newShifted, sameDay, shiftDate} from "@t/util/DateUtil.ts";
import {BUBBLE, Component, DONE, isCopyModifier, isInside} from "@/event/calendar/dom/Component.ts";
import {DrawingContext} from "@/event/calendar/dom/DrawingContext.ts";
import {CalendarView, DISPLAY_CUSTOMER, DISPLAY_DESCRIPTION, DISPLAY_PLACE, DISPLAY_SUBJECT, saveView} from "@/event/calendar/CalendarModel.ts";
import {MIN_SNAP_DISTANCE, ScrollComponent} from "@/event/calendar/dom/ScrollComponent.ts";
import {calculateEventLayers, CalEvent, DateRange, DUMMY_ID, EventRect, overlaps, normalizeRange, timeFormat, uidToPreviewKey} from "@/store/CalendarStore.ts";
import {COLOR_NAMES, DEFAULT_EVENT_COLOR} from "@t/fd/editor/EventColor.ts";
import {drawTextLine, TextRun} from "@/canvas/CanvasUtils.ts";
import {drawPlusBullet, MouseAction, MouseStartAction, PLUS_RADIUS, priorityBorderColor, strokeCanceled} from "@/event/calendar/days/DaysTimedBody.ts";
import {CanvasRootComponent} from "@/event/calendar/dom/CanvasRootComponent.ts";
import {getDataTransfer, hasIdentity, ParsedType, parseObjectRef, refToUID, setDataTransfer, uidToRef} from "@/utils/DragDropUtils.ts";
import {Icon} from "anneca-symbols";
import {DayColumn} from "@/event/calendar/days/DaysComponent.ts";
import {ALL_DAY_EVENT_HEIGHT} from "@/event/calendar/days/DaysWholeBody.ts";


const EVENT_HEIGHT = 19;
const MORE_BUTTON_RADIUS = 8;
const MORE_BUTTON_Y_OFFSET = 0;
const MORE_BUTTON_X_OFFSET = -11.5;
const DAY_MILLIS = 24 * 3600000;

export class WeeksBodyComponent extends Component {
	private view: CalendarView;
	/** Calculated at drawBackground and used for other drawings */
	private cells: GridCells;
	/** Create new placeholder */
	private createDummy: CalEvent;
	private eventShapes:EventRect[];
	private actionObject:EventRect;
	private action: MouseAction;
	private startX:number;
	private startY:number;
	private startDay: Date;	// The time where users started to manipulate event
	private startEventRange: DateRange;	// Original event time span

	/** Range of drop cursor */
	private dropFrom:Date|null;
	private dropTo:Date|null;

	/** Time of the Date of slot where user hovers over more button */
	private moreHover:number;

	constructor(view: CalendarView) {
		super();
		this.view = view;
		this.cells = null;
		this.createDummy = null;
		this.action = "idle";
		this.actionObject = null;
	}

	monthGridWeeks():number {
		const mgw = this.view.weeks;
		if (typeof mgw != "number") return 5;
		if (mgw < 1) return 1;
		if (mgw > 12) return 12;
		return mgw;
	}

	/**
	 * Change selected date but also alter scroll in such way that
	 * visually the component stays at the same position.
	 * After calling this method there shouldn't be any reason to call dirty()
	 * because the view should look the same as before.
	 * @param newDate for view.selected
	 */
	updateScrollForNewSelectedDate(newDate:Date):void {
		if (this.parent instanceof ScrollComponent) {
			const scroll = this.parent as ScrollComponent;
			const weekHeight = this.height / this.monthGridWeeks();
			const currentAlignDate = selectedDateToAligned(this.view.selected);
			const newAlignDate = selectedDateToAligned(newDate);
			// Get the time difference in milliseconds
			const timeDifference = newAlignDate.getTime() - currentAlignDate.getTime();
			// Convert the time difference to weeks

			const weekDifference = Math.round(timeDifference / (3600000 * 24 * 7));
			scroll.scrollY -= weekDifference * weekHeight;
			this.view.selected = newDate;
			this.view.aligned = newAlignDate;
			saveView(this.view);
		}
	}

	scrollIdle() {
		setNewDateAfterScrolling(this.view, this.cells);
	}

	/**
	 * Return date of the first fully visible grid cell. This cell is top-left corner of the grid.
	 * Partially visible cells doesn't count.
	 */
	firstVisibleDate():Date {
		if (this.parent instanceof ScrollComponent) {
			const scroll = this.parent as ScrollComponent;
			const weekHeight = this.height / this.monthGridWeeks();
			const currentAlignDate = selectedDateToAligned(this.view.selected);
			const fullWeeks = Math.floor((scroll.scrollY + 0.9 * weekHeight) / weekHeight);
			shiftDate(currentAlignDate, fullWeeks * 7);
			return currentAlignDate;
		}
	}


	protected drawBackground(dc: DrawingContext, area: Rect) {

		const hgrid = distributeGridSpace(this.width, 7);
		const height = this.height / this.monthGridWeeks();

		dc.ctx.fillStyle = dc.style.getPropertyValue("--cal-bg");
		dc.ctx.fillRect(area.x, area.y, area.width, area.height);

		// Fill weekends
		dc.ctx.fillStyle = dc.style.getPropertyValue("--cal-weekend-bg");
		const wi = weekInfo();
		if (wi.firstDay === 7) {
			// Week starts with sunday
			dc.ctx.fillRect(0, area.y, hgrid[0].size, area.height);
			dc.ctx.fillRect(hgrid[6].start, area.y, hgrid[6].size, area.height);
		} else {
			// Week starts with monday
			dc.ctx.fillRect(hgrid[5].start, area.y, hgrid[5].size + hgrid[6].size + 1, area.height);
		}


		this.cells = calcGridCells(hgrid, height, this.view, area.y, area.height);
		if (this.cells.today) {
			dc.ctx.fillStyle = dc.style.getPropertyValue("--cal-today-body");
			const b = this.cells.today.bounds;
			dc.ctx.fillRect(b.x, b.y, b.width, b.height);
		}

		const daySepar = dc.style.getPropertyValue("--cal-day-separ");
		const monthSepar = dc.style.getPropertyValue("--cal-header-separ");
		dc.ctx.strokeStyle = daySepar;
		for (let i = 0; i < hgrid.length-1; i++) {
			const s = hgrid[i];
			dc.ctx.beginPath();
			dc.ctx.moveTo(s.gapLine, area.y);
			dc.ctx.lineTo(s.gapLine, area.y + area.height);
			dc.ctx.stroke();
		}

		// Draw cells
		const fontFamily = dc.style.getPropertyValue("--font");
		dc.ctx.font = "bold 12px "+fontFamily;

		const pillBg = dc.style.getPropertyValue("--cal-today-bg");
		const pillFg = dc.style.getPropertyValue("--cal-today-fg");

		const currentMonthLabelFg = dc.style.getPropertyValue("--cal-workday-fg");
		const currentMonthWeekendLabelFg = dc.style.getPropertyValue("--cal-weekend-fg");
		const otherMonthLabelFg = currentMonthLabelFg+"80";
		const otherMonthWeekendLabelFg = currentMonthWeekendLabelFg+"80";

		for (let w = 0; w < this.cells.weeks.length; w++) {
			const wc = this.cells.weeks[w];
			for (let d = 0; d < wc.days.length; d++) {
				const daycell = wc.days[d];
				const dayOfWeek = daycell.date.getDay();
				const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
				const dd = daycell.date.getDate();
				let cellTag:string = "";
				let tagSuffix:string = "";
				if (dd === 1) {
					cellTag = dayNumeric(daycell.date);
					const lastChar = cellTag.charAt(cellTag.length - 1);
					if (/^\d$/.test(lastChar)) {
						tagSuffix = "";
					} else {
						cellTag = cellTag.substring(0, cellTag.length - 1);	// strip last char
						tagSuffix = lastChar;
					}
				} else {
					cellTag = dd.toString();
					tagSuffix = ".";
				}
				const tagX = daycell.bounds.x + MONTH_GRID_OFFSET_X;
				const tagY = daycell.bounds.y + MONTH_GRID_OFFSET_Y;
				let dayLabelRect;
				if (daycell.today) {
					dayLabelRect = drawTextBadge(dc.ctx, cellTag, tagX, tagY, 6, 2, pillBg, pillFg, tagSuffix);
				} else {
					let labelFg = isWeekend ? (daycell.current ? currentMonthWeekendLabelFg : otherMonthWeekendLabelFg) : (daycell.current ? currentMonthLabelFg : otherMonthLabelFg);
					dayLabelRect = drawTextBadge(dc.ctx, cellTag, tagX, tagY, 6, 2, null, labelFg, tagSuffix);
				}
				daycell.labelRect = dayLabelRect;

				// Holiday
				const h = this.view.store.getHoliday(daycell.date);
				if (h) drawHoliday(dc.ctx, h, daycell.bounds, fontFamily, pillBg);


				if (d === wc.days.length - 1) {
					// After last day
					// Draw line under the week
					const y = daycell.bounds.y + daycell.bounds.height;
					if (y < (area.y + area.height - 2) && y > (area.y + 2)) {
						dc.ctx.beginPath();
						dc.ctx.moveTo(area.x, y);
						dc.ctx.lineTo(area.x + area.width, y);
						dc.ctx.stroke();
					}
				}
				if (daycell.borderTop || daycell.borderLeft) {
					dc.ctx.lineWidth = 3;
					dc.ctx.strokeStyle = monthSepar;
					if (daycell.borderLeft) {
						const s = hgrid[d-1];
						dc.ctx.beginPath();
						dc.ctx.moveTo(s.gapLine, daycell.bounds.y);
						dc.ctx.lineTo(s.gapLine, daycell.bounds.y + daycell.bounds.height);
						dc.ctx.stroke();
					}
					if (daycell.borderTop) {
						dc.ctx.beginPath();
						dc.ctx.moveTo(daycell.bounds.x - 1.5, daycell.bounds.y);
						dc.ctx.lineTo(daycell.bounds.x + 1.5 + daycell.bounds.width, daycell.bounds.y);
						dc.ctx.stroke();
					}
					dc.ctx.lineWidth = 1;
					dc.ctx.strokeStyle = daySepar;
				}
			}
		}
	}

	private sortForDisplay(events:CalEvent[]):CalEvent[] {
		const copy:CalEvent[] = [ ...events ];
		copy.sort((e1:CalEvent, e2:CalEvent) => {
			let val = e1.start.getTime() - e2.start.getTime();
			if (val === 0) {
				val = compareAllDay(e1, e2);
				if (val === 0) {
					val = comparePriority(e1, e2);
				}
			}
			return val;
		});
		return copy;
	}

	protected drawContent(dc: DrawingContext, area: Rect) {
		const firstCellDate = this.cells.weeks[0].days[0].date;	// first cell of the first week
		const lastWeek = this.cells.weeks[this.cells.weeks.length - 1];
		const lastCellDate = lastWeek.days[lastWeek.days.length - 1].date;	// last cell of the last week
		const r = normalizeRange(firstCellDate, lastCellDate);

		const allEvents:CalEvent[] = this.sortForDisplay(this.view.store.getEvents(r.start, r.end));
		if (this.createDummy && overlaps(r, { start: this.createDummy.start, end: this.createDummy.end })) allEvents.splice(0, 0, this.createDummy);

		const layers: CalEvent[][] = calculateEventLayers(allEvents, true);
		const eventShapes:EventRect[] = [];

		const topOffset = MONTH_GRID_OFFSET_Y + 5;

		// Calculate how many levels can calendar hold
		let weekHeight = this.cells.weeks[0].days[0].bounds.height;
		weekHeight -= topOffset + 1;
		//let levelsFullyVisible = Math.floor(weekHeight / (EVENT_HEIGHT+1));
		let levelsLegiblyVisible = Math.floor((weekHeight + EVENT_HEIGHT / 2) / (EVENT_HEIGHT + 1));
		let levelsVisible = Math.ceil(weekHeight / (EVENT_HEIGHT+1));


		for (let l = 0; l < layers.length; l++) {
			const layer = layers[l];
			for (let e of layer) {
				for (let week of this.cells.weeks) {
					const firstDayBounds = week.days[0].bounds;
					const lastDayBounds = week.days[week.days.length - 1].bounds;
					const weekRun = buildWeekRun(e, week);
					if (weekRun) {
						if (l >= levelsLegiblyVisible) {
							// Mark run cells with needMore flag
							for (let i = 0; i < weekRun.span; i++) {
								week.days[weekRun.indexStart + i].more = true;
							}
						}
						if (l >= levelsVisible) continue;	// will be clipped anyway
					} else continue;
					try {
						dc.ctx.save();
						dc.ctx.beginPath();
						dc.ctx.rect(firstDayBounds.x, firstDayBounds.y, lastDayBounds.x - firstDayBounds.x + lastDayBounds.width, firstDayBounds.height);
						dc.ctx.clip();
						let y = week.days[0].bounds.y + l * (EVENT_HEIGHT+1) + 1 + topOffset;
						let erect = drawWeekEventRow(dc, e, weekRun.xStart, y, weekRun.xEnd - weekRun.xStart, EVENT_HEIGHT, weekRun.openStart, weekRun.openEnd, false, this.view);
						if (erect && e.uid !== DUMMY_ID) {
							erect.clipRect = { x: firstDayBounds.x, y: firstDayBounds.y, width: lastDayBounds.x - firstDayBounds.x + lastDayBounds.width, height: firstDayBounds.height };
							eventShapes.push(erect);
							for (let i = weekRun.indexStart; i < week.days.length && i < (weekRun.indexStart+weekRun.span); i++) {
								const daycell = week.days[i];
								let nextY = l * (EVENT_HEIGHT+1) + 1 + topOffset + erect.height + 1;
								if (nextY > (daycell.bounds.height - EVENT_HEIGHT - 1)) nextY = daycell.bounds.height - EVENT_HEIGHT - 1;
								if (daycell.dropY < nextY) daycell.dropY = nextY;	// increase position where it is possible to show drop cursor
							}
						}
					} finally {
						dc.ctx.restore();
					}
				}
			}
		}
		this.eventShapes = eventShapes;


		/** Optionally draw drop cursor */
		if (this.dropFrom && this.dropTo) {
			const isCopy = ("copy" === this.cursor);
			const selBg = dc.style.getPropertyValue("--selection-bg");
			dc.ctx.strokeStyle = selBg;
			if (selBg.startsWith("#")) dc.ctx.fillStyle = selBg + "55";	// add opacity to hex color
			else dc.ctx.fillStyle = selBg;
			dc.ctx.lineWidth = 2;

			for (let week of this.cells.weeks) {
				const firstDayBounds = week.days[0].bounds;
				const lastDayBounds = week.days[week.days.length - 1].bounds;
				const weekRun = buildWeekRunRange(this.dropFrom, this.dropTo, week);
				if (weekRun) {
					try {
						dc.ctx.save();
						dc.ctx.beginPath();
						dc.ctx.rect(firstDayBounds.x, firstDayBounds.y, lastDayBounds.x - firstDayBounds.x + lastDayBounds.width, firstDayBounds.height);
						dc.ctx.clip();
						const dropY = week.days[weekRun.indexStart].dropY;
						let y = week.days[0].bounds.y + dropY;
						let radiusTopLeft = 5, radiusTopRight = 5, radiusBottomRight = 5, radiusBottomLeft = 5;
						if (weekRun.openStart) radiusTopLeft = radiusBottomLeft = 0;
						if (weekRun.openEnd) radiusTopRight = radiusBottomRight = 0;
						const r = insetRect({x: weekRun.xStart, y, width: (weekRun.xEnd - weekRun.xStart), height: EVENT_HEIGHT}, 2);
						roundFillShape(dc.ctx, r, radiusTopLeft, radiusTopRight, radiusBottomRight, radiusBottomLeft);
						dc.ctx.fill();
						dc.ctx.stroke();
						if (isCopy && !weekRun.openStart) {
							const cx = r.x + 2.5 + PLUS_RADIUS;
							const cy = r.y + EVENT_HEIGHT/2 + PLUS_RADIUS - 1;
							drawPlusBullet(dc, cx, cy);
						}
					} finally {
						dc.ctx.restore();
					}
				}
			}
		}


		/** Paint more button */
		const moreButtonFg = dc.style.getPropertyValue("--red");
		const moreButtonBg = dc.style.getPropertyValue("--cal-bg");
		dc.ctx.strokeStyle = moreButtonFg;
		for (let week of this.cells.weeks) {
			for (let day of week.days) {
				if (day.more) {
					const b = day.bounds;
					dc.ctx.fillStyle = this.moreHover === day.date.getTime() ? moreButtonFg: moreButtonBg;

					dc.ctx.beginPath();
					const dotY = b.y + b.height + MORE_BUTTON_Y_OFFSET;
					dc.ctx.ellipse(b.x + b.width + MORE_BUTTON_X_OFFSET, dotY, 8, 8, 0, 0, 2 * Math.PI);
					dc.ctx.fill();
					dc.ctx.stroke();

					dc.ctx.beginPath();
					dc.ctx.fillStyle = this.moreHover === day.date.getTime() ? moreButtonBg : moreButtonFg;
					dc.ctx.ellipse(b.x + b.width + MORE_BUTTON_X_OFFSET, dotY, 1, 1.2, 0, 0, 2 * Math.PI);
					dc.ctx.ellipse(b.x + b.width + MORE_BUTTON_X_OFFSET - 4, dotY, 1, 1.2, 0, 0, 2 * Math.PI);
					dc.ctx.ellipse(b.x + b.width + MORE_BUTTON_X_OFFSET + 4, dotY, 1, 1.2, 0, 0, 2 * Math.PI);
					dc.ctx.fill();

					// Hover area
					day.moreShape = new Path2D();
					day.moreShape.ellipse(b.x + b.width + MORE_BUTTON_X_OFFSET, dotY, 8.5, 8.5, 0, 0, 2 * Math.PI);
				}
			}
		}
	}

	getSnapPosition(x: number, y: number, xPositiveDir?:boolean, yPositiveDir?:boolean): SnapCoordinates | null {
		const height = this.height / this.monthGridWeeks();

		const lowerSnapY = Math.floor(y / height) * height;
		const higherSnapY = Math.ceil(y / height) * height;
		const lowerDistance = Math.abs(y - lowerSnapY);
		const higherDistance = Math.abs(y - higherSnapY);
		let snapY;
		if (lowerDistance < MIN_SNAP_DISTANCE && higherDistance >= MIN_SNAP_DISTANCE) snapY = lowerSnapY;
		else if (higherDistance < MIN_SNAP_DISTANCE && lowerDistance >= MIN_SNAP_DISTANCE) snapY = higherSnapY;
		else snapY = yPositiveDir ? higherSnapY : lowerSnapY;

		if (y === snapY) return null;
		return { x: 0, y: snapY, snapX: false, snapY: true };
	}

	cursorToDay(x: number, y: number):Date {
		for (let week of this.cells.weeks) {
			for (let day of week.days) {
				if (isInside(x, y, day.bounds)) {
					return new Date(day.date);
				}
			}
		}
		return null;
	}


	determineStartAction(e: PointerEvent, x: number, y: number):{ action: MouseStartAction, er?:EventRect, more?:DayCell } {
		const ctx = this.root().ctx;
		let oldMoreHover = this.moreHover;
		try {
			if (this.cells) {
				for (let week of this.cells.weeks) {
					for (let day of week.days) {
						if (day.more) {
							if (day.moreShape && ctx.isPointInPath(day.moreShape, x, y)) {
								this.moreHover = day.date.getTime();
								return {action: "button", more: day};
							} else {
								this.moreHover = 0;
							}
						}
						if (day.labelRect && isInside(x, y, day.labelRect)) {
							return { action: "label", more: day };
						}
					}
				}
			}
		} finally {
			if (oldMoreHover !== this.moreHover) this.dirty(false);
		}
		for (let er of this.eventShapes) {
			if (er.clipRect) {
				if (!isInside(x, y, er.clipRect)) continue;
			}
			if (er.startDragShape && ctx.isPointInPath(er.startDragShape, x, y)) {
				return { action: "start", er: er };
			}
			if (er.endDragShape && ctx.isPointInPath(er.endDragShape, x, y)) {
				return { action: "end", er: er };
			}
			if (ctx.isPointInPath(er.shape, x, y)) {
				return { action: "event", er: er };
			}
		}
		return { action: "bg" };
	}


	onPointerMove(e: PointerEvent, x: number, y: number): boolean {
		if (e.pointerType === "mouse") {
			this.cursor = null;
			if (this.action === "idle") {
				const { action, er} = this.determineStartAction(e, x, y);
				if (action === "event") this.cursorInPreviewObject(er, uidToPreviewKey(er.event.uid));
				else this.cursorOutOfPreviewObject();
				switch (action) {
					case "button":
					case "label":
						this.cursor = "pointer";
						break;
					case "start":
						this.cursor = "ew-resize";
						break;
					case "end":
						this.cursor = "ew-resize";
						break;
					case "event":
						this.cursor = "pointer";
						break;
					case "bg":
						this.cursor = "default";
						break;
				}
			} else if (this.action === "bg" || this.action === "start" || this.action === "end") {
				// Waiting to move to start action and keeping the cursor
				const reallyStart:boolean = (Math.max(Math.abs(this.startX - x), Math.abs(this.startY - y))) > 2;
				if (reallyStart) {
					// Mouse moved enough now we know
					switch (this.action) {
						case "bg":
							this.action = "create";
							this.cursor = "default";

							let dummyStart = new Date(this.startDay),
								dummyEnd = this.cursorToDay(x, y);
							if (dummyStart.getTime() > dummyEnd.getTime()) {
								const temp = dummyStart;
								dummyStart = dummyEnd;
								dummyEnd = temp;
							}
							dummyStart.setHours(0, 0, 0, 0);
							dummyEnd.setHours(0, 0, 0, 0);
							shiftDate(dummyEnd);

							const root = this.root();
							this.createDummy = this.dummyEvent(dummyStart, dummyEnd);
							this.dirty(false);
							break;
						case "start":
							this.action = "resize-start";
							this.cursor = "ew-resize";
							break;
						case "end":
							this.action = "resize-end";
							this.cursor = "ew-resize";
							break;
					}
				}
			} else {
				let time;
				if (this.actionObject) {
					switch (this.action) {
						case "resize-start":
							time = this.cursorToDay(x, y);
							if (time) {
								time.setHours(0, 0, 0, 0);
								if (time.getTime() < this.actionObject.event.end.getTime())	this.actionObject.event.start = time;
							}
							this.cursor = "ew-resize";
							break;
						case "resize-end":
							time = this.cursorToDay(x, y);
							if (time) {
								time.setHours(0, 0, 0, 0);
								shiftDate(time);
								if (time.getTime() > this.actionObject.event.start.getTime()) this.actionObject.event.end = time;
							}
							this.cursor = "ew-resize";
							break;
					}
					this.dirty(false);
				} else {
					if (this.action === "create") {

						let dummyStart = new Date(this.startDay),
							dummyEnd = this.cursorToDay(x, y);
						if (dummyEnd) {
							if (dummyStart.getTime() > dummyEnd.getTime()) {
								const temp = dummyStart;
								dummyStart = dummyEnd;
								dummyEnd = temp;
							}
							dummyStart.setHours(0, 0, 0, 0);
							dummyEnd.setHours(0, 0, 0, 0);
							shiftDate(dummyEnd);
							this.createDummy.start = dummyStart;
							this.createDummy.end = dummyEnd;
							this.cursor = "ew-resize";
							this.dirty(false);
						}
					}
				}
			}
		}
		return BUBBLE;
	}

	dummyEvent(start: Date, end: Date): CalEvent {
		return { uid: DUMMY_ID, allDay: true,
			title: this.root().env.msg("event.create.dummy"),
			color: (this.view.defaultColor ?? DEFAULT_EVENT_COLOR),
			start, end };
	}

	onPointerDown(e: PointerEvent, x: number, y: number): boolean {
		this.startX = x;
		this.startY = y;

		this.startDay = this.cursorToDay(x, y);
		const { action, er, more } = this.determineStartAction(e, x, y);
		this.action = action;
		this.actionObject = er;

		if (e.pointerType === "mouse") {
			switch (action) {
				case "start":
					this.startEventRange = {start: er.event.start, end: er.event.end};
					e.preventDefault();
					this.setPointerCapture(e);
					return false;
				case "end":
					this.startEventRange = {start: er.event.start, end: er.event.end};
					e.preventDefault();
					this.setPointerCapture(e);
					return false;
				case "event":
					if (er.event.movable) {
						this.root().canvas.setAttribute("draggable", "true");
						this.startEventRange = {start: er.event.start, end: er.event.end};
						return false;
					}
					break;
				case "bg":
					e.preventDefault();
					this.setPointerCapture(e);
					return false;
				case "button":
				case "label":
					return false;
			}
			return true;
		} else {
			// Touch & Pen devices
			switch (action) {
				case "start":
				case "end":
				case "event":
					this.action = "event";	// ignore start/end
					if (er.event.movable) {
						this.root().canvas.setAttribute("draggable", "true");
						this.startEventRange = { start: er.event.start, end: er.event.end };
						return DONE;
					}
					break;
				case "bg":
					this.action = "event";
					this.root().canvas.setAttribute("draggable", "true");
					const start = new Date(this.startDay);
					start.setHours(0, 0, 0, 0);
					const end = newShifted(start, 1);
					this.actionObject = this.buildEventRect(this.dummyEvent(start, end), x, y);
					this.startEventRange = { start, end };
					return DONE;
				case "button":
				case "label":
					return DONE;
				default:
					this.action = "idle";
					this.actionObject = null;
			}
			return BUBBLE;
		}
	}

	buildEventRect(e:CalEvent, x: number, y:number):EventRect {

		let d:DayCell;
		for (let week of this.cells.weeks) {
			for (let day of week.days) {
				if (isInside(x, y, day.bounds)) {
					d = day;
					break;
				}
			}
		}

		const eventX = d.bounds.x + 1;
		const eventWidth = d.bounds.width - 2;
		const eventY = y - ALL_DAY_EVENT_HEIGHT / 2;
		const eventHeight = ALL_DAY_EVENT_HEIGHT;

		const shape = new Path2D();
		shape.roundRect(eventX, eventY, eventWidth, eventHeight, ALL_DAY_EVENT_HEIGHT/2);

		const er:EventRect = {
			x:eventX, y: eventY, width: eventWidth, height: eventHeight,
			event:e,
			shape
		};
		return er;
	}


	onPointerUp(e: PointerEvent, x: number, y: number): boolean {
		if (e.pointerType === "mouse") {
			switch (this.action) {
				case "idle":	// nothing happens
					break;
				case "bg":	// candidates for future action
					this.view.store.manipulationEnd();
					let dummyStart = new Date(this.startDay),
						dummyEnd = this.cursorToDay(x, y);
					if (dummyStart.getTime() > dummyEnd.getTime()) {
						const temp = dummyStart;
						dummyStart = dummyEnd;
						dummyEnd = temp;
					}
					dummyStart.setHours(0, 0, 0, 0);
					dummyEnd.setHours(0, 0, 0, 0);
					shiftDate(dummyEnd);
					this.view.store.invokeCreate(true, dummyStart, dummyEnd);
					break;
				case "start":
				case "end":
					this.view.store.manipulationEnd();
					break;
				case "create":
					this.view.store.invokeCreate(true, this.createDummy.start, this.createDummy.end);
					this.createDummy = null;
					break;
				case "resize-start":
					const newStart = this.actionObject.event.start;
					this.actionObject.event.start = this.startEventRange.start;
					this.view.store.update(this.actionObject.event.uid).setStart(newStart).commit();
					this.view.store.manipulationEnd();
					break;
				case "resize-end":
					const newEnd = this.actionObject.event.end;
					this.actionObject.event.end = this.startEventRange.end;
					this.view.store.update(this.actionObject.event.uid).setEnd(newEnd).commit();
					this.view.store.manipulationEnd();
					break;
				case "button":
					if (this.moreHover > 0) {
						this.fireEvent("action", {id: "viewDays", day: new Date(this.moreHover) });
					}
					break;
				case "label":
					const { action, more } = this.determineStartAction(e, x, y);
					if (action === "label") {
						this.fireEvent("action", {id: "viewDays", day: new Date(more.date) });
					}
					break;
				case "event":
					this.view.store.invokeEdit(this.actionObject.event.uid);
					break;
			}
			this.action = "idle";
			this.actionObject = null;
			return true;
		} else {
			// Touch & Pen devices
			if (this.action === "event") {
				// may be existing or new
				if (this.actionObject.event.uid === DUMMY_ID) {
					this.view.store.invokeCreate(true, this.actionObject.event.start, this.actionObject.event.end);
				} else {
					this.view.store.invokeEdit(this.actionObject.event.uid);
				}
			} else if (this.action === "button") {
				if (this.moreHover > 0) {
					this.fireEvent("action", {id: "viewDays", day: new Date(this.moreHover) });
				}
			} else if (this.action === "label") {
				const { action, more } = this.determineStartAction(e, x, y);
				if (action === "label") {
					this.fireEvent("action", {id: "viewDays", day: new Date(more.date) });
				}
			}
			this.action = "idle";
			this.actionObject = null;
			return true;
		}
	}


	onDragStart(e: DragEvent, x: number, y: number) {
		super.onDragStart(e, x, y);
		// Set custom drag image
		const root = this.root();
		const dragImage = createEventDragImage(this.actionObject, root); // Function to create your custom drag image
		const offsetX = Math.floor(this.startX - this.actionObject.x);
		const offsetY = Math.floor(this.startY - this.actionObject.y);
		e.dataTransfer.setDragImage(dragImage, offsetX, offsetY);

		// Calculate dayShift
		let dayShift, eventLength;
		if (this.actionObject.event.allDay) {
			const grabDate = this.cursorToDay(this.startX, this.startY);	// The date grabbed by user
			grabDate.setHours(0, 0, 0, 0);
			const startDate = new Date(this.actionObject.event.start);
			startDate.setHours(0, 0, 0, 0);
			dayShift = -1 * Math.round((grabDate.getTime() - startDate.getTime()) / DAY_MILLIS);
			eventLength = Math.round((this.actionObject.event.end.getTime() - this.actionObject.event.start.getTime()) / DAY_MILLIS);
		} else {
			dayShift = 0;
			eventLength = 1;
		}

		// Allow for both copy and move
		e.dataTransfer.effectAllowed = 'copyMove';
		// Optionally, set data in the dataTransfer object
		setDataTransfer(e.dataTransfer, { ox:offsetX, oy:offsetY, ds: dayShift, len: eventLength }, uidToRef(this.actionObject.event.uid));

		this.actionObject.event.state = "move";
		this.dirty(false);
		return false;
	}

	onDragEnd(e: DragEvent, x: number, y: number) {
		this.root().canvas.removeAttribute('draggable');
		this.actionObject.event.state = "normal";
		this.dropFrom = this.dropTo = null;
		this.dirty(false);
		return true;
	}

	onDragEnter(e: DragEvent, x: number, y: number) {
		return this.onDragOver(e, x, y);
	}

	onDragLeave(e: DragEvent, x: number, y: number) {
		const change = this.dropFrom != null || this.dropTo != null;
		this.dropFrom = null;
		this.dropTo = null;
		if (change) this.dirty(false);
		return true;
	}

	onDragOver(e: DragEvent, x: number, y: number) {
		// Find data type we can accept
		let pt:ParsedType = getDataTransfer(e.dataTransfer);
		if (pt) {
			let dayShift = typeof pt.params?.ds === "string" ? parseInt(pt.params.ds) : 0;
			let eventLength = typeof pt.params?.len === "string" ? parseInt(pt.params.len) : 1;

			const eventStart = this.cursorToDay(x, y);
			if (eventStart) {
				shiftDate(eventStart, dayShift);
				eventStart.setHours(0, 0, 0, 0);
				const eventEnd = newShifted(eventStart, eventLength);

				e.preventDefault();	// accepting drop
				// Detect modifier key (Ctrl typically means copy)
				if (isCopyModifier(e)) {
					e.dataTransfer.dropEffect = 'copy';
					this.setCursorForDragEffect("copy");
				} else {
					e.dataTransfer.dropEffect = 'move';
					this.setCursorForDragEffect("move");
				}

				const change = (this.dropFrom == null || this.dropFrom.getTime() !== eventStart.getTime()) || (this.dropTo == null || this.dropTo.getTime() !== eventEnd.getTime());
				if (change) {
					this.dropFrom = eventStart;
					this.dropTo = eventEnd;
					this.dirty(false);
				}
			}
		}
		return true;
	}

	onDrop(e: DragEvent, x: number, y: number) {
		this.dropFrom = this.dropTo = null;	// remove marker
		let pt:ParsedType = getDataTransfer(e.dataTransfer);
		if (pt) {
			let dayShift = typeof pt.params?.ds === "string" ? parseInt(pt.params.ds) : 0;
			let eventLength = typeof pt.params?.len === "string" ? parseInt(pt.params.len) : 1;

			const eventStart = this.cursorToDay(x, y);
			if (eventStart) {
				e.preventDefault();	// accepting drop
				const effect = e.dataTransfer.dropEffect; // TODO 'copy' or 'move'
				console.log("Drop effect = " + effect);

				let oref = parseObjectRef(e.dataTransfer.getData(pt.full));
				if (oref) {
					if (hasIdentity(oref)) {
						const isCopy = this.cursor === "copy";
						const currentEvent = this.view.store.findByUID(refToUID(oref));
						if (currentEvent.allDay) {
							shiftDate(eventStart, dayShift);
							eventStart.setHours(0, 0, 0, 0);
							if (isCopy) this.view.store.invokeCopy(true, eventStart, oref);
							else this.view.store.update(oref).move(eventStart).commit();
						} else {
							eventStart.setHours(currentEvent.start.getHours(), currentEvent.start.getMinutes(), 0, 0);
							if (isCopy) this.view.store.invokeCopy(false, eventStart, oref);
							else this.view.store.update(oref).move(eventStart).commit();
						}
					} else {
						const dend = newShifted(eventStart, 1);
						this.view.store.invokeCreate(true, eventStart, dend, oref);
					}
				}
			}
		}
		this.dirty(false);
		return true;
	}

}


////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////




/** Longer events first */
function compareByDuration(e1:CalEvent, e2:CalEvent):number {
	const d1 = e1.end.getTime() - e1.start.getTime();
	const d2 = e2.end.getTime() - e2.start.getTime();
	return d2 - d1;
}

export function compareComplete(e1:CalEvent, e2:CalEvent):number {
	const c1 = e1.completed || e1.canceled ? 1 : 0;
	const c2 = e2.completed || e2.canceled ? 1 : 0;
	return c1 - c2;
}

export function compareAllDay(e1:CalEvent, e2:CalEvent):number {
	if (e1.allDay) {
		if (e2.allDay) return compareByDuration(e1, e2);	// if both events are all-day, longer events will be first
		return -1;
	} else {
		if (e2.allDay) return 1;
		return 0;
	}
}

export function comparePriority(e1:CalEvent, e2:CalEvent):number {
	const p1 = e1.priority ?? 10;
	const p2 = e2.priority ?? 10;
	return p1 - p2;		// high priority should be first (<0)
}


interface GridRect extends Rect {
	/** Height of grid row */
	row: number;
	/** Width of grid column */
	column: number;
}

interface EventPlacement {
	event: CalEvent;
	level: number;
}

/** Represents one day in month grid */
export interface DayCell {
	date: Date,
	today: boolean,		// is today
	focused: boolean,	// is focused day
	current: boolean,	// is current month (is the most visible month)
	bounds: Rect,		// position of the cell
	more?: boolean,		// show more button to display clipped events
	moreShape?: Path2D,	// Shape of more button to detect :hover
	labelRect?: Rect;
	borderTop?: boolean,
	borderLeft?: boolean,
	dropY?: number		// Position of drop cursor
}

/** Cells for one week row */
export interface WeekCells {
	days: DayCell[],
}

export interface GridCells {
	/** Identification of a month which is dominantly visible (month which has the most cells in the view) */
	month: number,
	year: number,
	weeks: WeekCells[]
	today?: DayCell;	/* Cell of today (to draw correct background) */
}

function selectedDateToAligned(selected:Date):Date {
	const f = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate()); // normalized selected
	f.setHours(12, 0, 0, 0);	// align to noon

	const wi = weekInfo();
	const dateFD = wi.firstDay % 7; // week info has different numbering of weekdays then Date() class
	while (f.getDay() !== dateFD) {
		shiftDate(f, -1); // align
	}
	// f is now aligned to the first day of week containing focused date
	return f;
}

/**
 * Calculate all visible date cells
 * @param h horizontal slots precalculated by dividing available space
 * @param v vertical height in pixels
 * @param view calendar
 * @param scrollY current scroll; scrollY is negative if the grid is scrolled into past, positive value means we go to the future
 * @param gridHeight total height of grid view
 */
function calcGridCells(h:Slot[], v:number, view: CalendarView, scrollY: number, gridHeight:number):GridCells {
	const today = new Date();
	view.aligned = selectedDateToAligned(view.selected);
	let f = new Date(view.aligned);

	// Now we have to find the first visible row/week
	const weekShift = Math.floor(scrollY / v);	// number of weeks to shift
	shiftDate(f, 7 * weekShift);	// now f points to the first visible cell @ top left corner

	const grid:GridCells = { month: 0, year: 0, weeks: [] };

	let w = 0;
	let weekTopEdge = weekShift * v;
	while (weekTopEdge < (scrollY + gridHeight)) {
		const wc:WeekCells = { days: [] };
		for (let d = 0; d < 7; d++) {
			const cellDate = new Date(f);
			const slot = h[d];
			let borderTop = cellDate.getDate() <= 7;
			let borderLeft = cellDate.getDate() === 1 && d > 0;
			shiftDate(f);
			wc.days.push({
				current: false,
				focused: false,
				today: false,
				date: cellDate,
				bounds: { x: slot.start, y: weekTopEdge, width: slot.size , height: v },
				borderTop, borderLeft,
				dropY: 	MONTH_GRID_OFFSET_Y + 5

			})
		}
		grid.weeks.push(wc);
		w++;
		weekTopEdge = (weekShift + w) * v;
	}

	let counts: Map<number, number> = new Map();
	// Calculate focus month
	for (let w of grid.weeks) {
		for (let d of w.days) {
			const monthYear = d.date.getMonth() + (100 * d.date.getFullYear());
			if (counts.has(monthYear)) counts.set(monthYear, counts.get(monthYear) + 1);
			else counts.set(monthYear, 1);
		}
	}
	let maxMonthYear = 0;
	let maxCount = 0;
	counts.forEach((count, my) => {
		if (count > maxCount) {
			maxCount = count;
			maxMonthYear = my;
		}
	});
	grid.month = maxMonthYear % 100;
	grid.year = Math.floor(maxMonthYear / 100);
	// Mark days if belong to the month/year
	for (let w of grid.weeks) {
		for (let d of w.days) {
			if (d.date.getMonth() === grid.month && d.date.getFullYear() === grid.year) d.current = true;
			if (sameDay(d.date, today)) {
				d.today = true;
				grid.today = d;
			}
			//if (d.date.getDate() === 1) d.today = true;
		}
	}
	requestData(view, grid);
	return grid;
}

function requestData(view:CalendarView, grid:GridCells):void {
	let minDate = null;
	let maxDate = null;

	for (let w of grid.weeks) {
		for (let d of w.days) {
			if (minDate == null || d.date.getTime() < minDate.getTime()) minDate = d.date;
			if (maxDate == null || d.date.getTime() > maxDate.getTime()) maxDate = d.date;
		}
	}
	if (minDate != null) {
		view.store.needRange(minDate, maxDate, view.saveName ?? "main");
		view.focused = { start: minDate, end: maxDate };
	}
}

interface WeekRun {
	/* X axis position of the event row */
	xStart: number;
	xEnd: number;
	/* Specifies if the row should be rendered as open at start or end */
	openStart: boolean;
	openEnd: boolean;

	/** Index of the first cell of this event and number of spanned cells */
	indexStart:number;
	span:number;
}



function buildWeekRun(e: CalEvent, week: WeekCells):WeekRun|null {
	return buildWeekRunRange(e.start, e.end, week);
}

function buildWeekRunRange(start:Date, end:Date, week: WeekCells):WeekRun|null {
	let indexStart, indexEnd;
	const weekStart = new Date(week.days[0].date);
	const weekEnd = new Date(week.days[week.days.length - 1].date);
	weekStart.setHours(0, 0, 0, 0);
	weekEnd.setHours(0, 0, 0, 0);
	shiftDate(weekEnd);

	if (end.getTime() <= weekStart.getTime() || start.getTime() >= weekEnd.getTime()) return null;	// event not in this week
	const openStart = start.getTime() < weekStart.getTime();
	const openEnd = end.getTime() > weekEnd.getTime();
	let xStart, xEnd;

	indexStart = 0;
	if (openStart) {
		xStart = week.days[0].bounds.x;
	} else {
		for (let cell of week.days) {
			if (sameDay(start, cell.date)) {
				xStart = cell.bounds.x;
				break;
			}
			indexStart++;
		}
	}

	if (openEnd) {
		const lastCell = week.days[week.days.length - 1];
		xEnd = lastCell.bounds.x + lastCell.bounds.width;
		indexEnd = week.days.length - 1;	// last cell
	} else {
		const secBefore = new Date(end.getTime() - 1000);
		indexEnd = 0;
		for (let cell of week.days) {
			if (sameDay(secBefore, cell.date)) {
				xEnd = cell.bounds.x + cell.bounds.width;
				break;
			}
			indexEnd++
		}
	}
	if (!openStart) xStart++;
	if (!openEnd) xEnd--;
	return {
		xStart, xEnd,
		openStart, openEnd,
		indexStart,
		span: (indexEnd - indexStart + 1)
	}
}

/** Inset radius for given stroke */
function ir(radius: number, stroke: number) {
	return Math.max(0, radius - stroke / 2);
}

/**
 * Display event in one row. Suitable for all day events
 */
export function drawWeekEventRow(dc:DrawingContext, event:CalEvent, x:number, y:number, width:number, height:number, openStart:boolean, openEnd: boolean, shadow=false, view?:CalendarView):EventRect {
	const ff = dc.style.getPropertyValue("--font");
	let fg = dc.style.getPropertyValue("--cal-fg");
	let bg = dc.style.getPropertyValue("--cal-bg");
	try {
		dc.ctx.save();
		const dummy = DUMMY_ID === event.uid;

		let opacity:number = 1;
		if (event.state === "move") opacity = MOVE_OPACITY;
		if (event.canceled) opacity *= CANCELED_OPACITY;
		else if (event.completed) opacity *= DONE_OPACITY;
		if (opacity < 1) {
			dc.ctx.globalAlpha = opacity;
		}

		let c = event.color;
		if (typeof c !== "number" || c < 0 || c >= COLOR_NAMES.length) c = 3;	// default color
		const colorName = COLOR_NAMES[c];

		let boxColor = dc.style.getPropertyValue("--" + colorName + "-bg");
		if (event.completed || event.canceled) {
			let boxColorRGB = parseHexColor(boxColor);
			boxColorRGB = reduceSaturation(boxColorRGB.r, boxColorRGB.g, boxColorRGB.b, DONE_SATURATION);
			boxColor = rgbToHex(boxColorRGB);
		}
		dc.ctx.fillStyle = boxColor;

		const radius = 5;

		const er:Partial<EventRect> = { event, x, y, width, height };
		let radiusTopLeft = radius, radiusTopRight = radius, radiusBottomRight = radius, radiusBottomLeft = radius;
		if (openStart) radiusTopLeft = radiusBottomLeft = 0;
		if (openEnd) radiusTopRight = radiusBottomRight = 0;
		er.shape = roundRect({x, y, width, height}, radiusTopLeft, radiusTopRight, radiusBottomRight, radiusBottomLeft);
		if (shadow) {
			dc.ctx.shadowColor = "#00000022";
			dc.ctx.shadowBlur = 8;
			dc.ctx.shadowOffsetX = 1;
			dc.ctx.shadowOffsetY = 1;
		}
		if (event.allDay) {
			dc.ctx.fill(er.shape);
			// Border
			dc.ctx.strokeStyle = priorityBorderColor(event.priority, fg);;
			let bx = x, by = y, bwidth = width, bheight = height;
			if (openStart) {
				bx--;
				bwidth++;
			}
			if (openEnd) {
				bwidth++;
			}

			roundFillShape(dc.ctx, insetRect({ x:bx, y:by, width:bwidth, height:bheight}, 1), radiusTopLeft-0.5, radiusTopRight-0.5, radiusBottomRight-0.5, radiusBottomLeft-0.5);
			dc.ctx.stroke();

			// if (openStart) {
			// 	if (openEnd) {
			//
			// 	} else {
			//
			// 	}
			// } else {
			// 	if (openEnd) {
			//
			// 	} else {
			// 	}
			// }
		}
		if (shadow) {
			dc.ctx.shadowColor = 'transparent';
			dc.ctx.shadowBlur = 0;
		}
		if (dummy) {
			// Create 2px border around dummy event
			dc.ctx.strokeStyle = fg = dc.style.getPropertyValue("--selection-bg");
			dc.ctx.lineWidth = 2;
			roundFillShape(dc.ctx, insetRect({x, y, width, height}, 2),
				ir(radiusTopLeft, 2), ir(radiusTopRight, 2), ir(radiusBottomRight, 2), ir(radiusBottomLeft, 2));
			// dc.ctx.beginPath();
			// dc.ctx.roundRect(x + 1, y + 1, width - 2, height - 2, (height - 2)/2);
			dc.ctx.stroke();
		}
		dc.ctx.clip(er.shape);

		if (!event.allDay) {
			const bgRGB = parseHexColor(bg);
			dc.ctx.beginPath();
			dc.ctx.ellipse(x + 5, y + 9.5, 3, 3, 0, 0, 2 * Math.PI);
			if (grayscale(bgRGB) > 128) {
				// Detect light calendar with dark bg
				dc.ctx.strokeStyle = fg;
				dc.ctx.stroke();
			}
			dc.ctx.fill();
		}

		if (event.movable && event.allDay) {
			// Only all day events can be resized in this calendar view
			er.startDragShape = new Path2D();
			er.startDragShape.rect(x, y, 8, height);
			er.endDragShape = new Path2D();
			er.endDragShape.rect(x + width - 8, y, 8, height);
		}

		const PADX = 10;
		let tx = x + PADX;
		let twidth = width - 2 * PADX;

		if (twidth < 16) return;	// too little space to draw anything

		let fontSize = 10;
		const lh = Math.floor(fontSize * 1.5);
		fontSize = Math.floor(fontSize);
		const iconSize = Math.round(14 * fontSize / 12);

		let showTime = true;
		let display = DISPLAY_SUBJECT+DISPLAY_PLACE;
		let showAttendees: "no" | "mono" | "full" = "no";
		if (view) {
			showTime = view.showTime;
			display = view.display;
			showAttendees = view.showAttendees;
		}

		const runs:TextRun[] = [];
		const separ:TextRun = { text: " ~ ", font: fontSize+"px "+ff, color: fg, opacity: 0.5 };

		if (!event.allDay && showTime) {
			runs.push({ text: timeFormat(event.start)+" ", font: fontSize+"px "+ff, color: fg, opacity: 0.5 });
		}

		let first = true;
		for (let i = 0; i < display.length; i++) {
			switch (display.charAt(i)) {
				case DISPLAY_SUBJECT:
					if (first) first = false;
					else runs.push(separ);
					let title = event.title;
					if (event.icon) {
						const eicon = decodeNumericIconEntity(Icon.parse(event.icon).entity);
						runs.push({ text: eicon, font: iconSize+"px "+ICON_FONT, color: fg });
						title = " "+title;	// add space to separate from the icon
					}
					runs.push({ text: title, font: fontSize+"px "+ff, color: fg+(!event.allDay && (event.completed || event.canceled) ? "99":"") });
					break;
				case DISPLAY_PLACE:
					if (event.place) {
						if (first) first = false;
						else runs.push(separ);
						runs.push({ text: event.place, font: fontSize+"px "+ff, color: fg, opacity: 0.3 });
					}
					break;
				case DISPLAY_CUSTOMER:
					if (event.partyName) {
						if (first) first = false;
						else runs.push(separ);
						runs.push({ text: event.partyName, font: fontSize+"px "+ff, color: fg, opacity: 0.75 });
					}
					break;
				case DISPLAY_DESCRIPTION:
					if (event.comment) {
						if (first) first = false;
						else runs.push(separ);
						runs.push({ text: event.comment, font: fontSize+"px "+ff, color: fg, opacity: 0.5 });
					}
					break;
			}
		}
		if (showAttendees !== "no" && Array.isArray(event.attendees) && event.attendees.length > 0) {
			if (!first) runs.push(separ);
			const names:string[] = [];
			event.attendees.forEach((a) => {
				names.push(a.name);
			});
			runs.push({ text: names.join(", "), font: fontSize+"px "+ff, color: fg, opacity: 0.3 });
		}

		drawTextLine(dc.ctx, runs, tx, y+13, twidth);
		strokeCanceled(event, dc, x, y, width, height);

		return er as EventRect;
	} finally {
		dc.ctx.restore();
	}
}


function createEventDragImage(er: EventRect, root: CanvasRootComponent) {
	const canvas:HTMLCanvasElement = document.createElement('canvas');
	canvas.style.width = er.width + "px";
	canvas.style.height = er.height + "px";
	canvas.style.position = 'absolute';
	canvas.style.top = "-9000px";
	const ctx = canvas.getContext('2d');
	// Set canvas size
	canvas.width = root.ratio *  er.width;
	canvas.height = root.ratio *  er.height;
	ctx.scale(root.ratio, root.ratio);

	drawWeekEventRow({ ctx: ctx, style: root.style, env: root.env, props:{} }, er.event, 0, 0, er.width, er.height, false, false);

	document.body.appendChild(canvas);
	setTimeout(() => canvas.remove(), 0);
	return canvas;
}


function setNewDateAfterScrolling(view:CalendarView, cells:GridCells):void {
	const selected:Date = cells.weeks[0].days[0].date;	// first cell of the first week
	saveView(view, selected);
}